import { AnimatePresence, motion } from "motion/react";
import {
  ArrowLeft,
  ArrowRight,
  BookHeart,
  Check,
  CloudOff,
  LocateFixed,
  LockKeyhole,
  MoonStar,
  ShieldCheck,
  Sparkles
} from "lucide-react";
import { useState } from "react";
import { APP_NAME, APP_TAGLINE } from "../../app/brand";
import { useTheme } from "../../app/providers/ThemeProvider";
import { Button } from "../../components/ui/Button";
import { InputField } from "../../components/ui/FormField";
import { cn } from "../../lib/cn";
import { DEFAULT_ONBOARDING_STATE, type OnboardingState } from "./onboarding-storage";

const COLLECTIONS = ["Parents", "Family", "Akhirah", "Forgiveness", "Guidance", "Health", "Rizq", "Friends", "Ummah"];

interface OnboardingProps {
  onComplete: (state: OnboardingState) => Promise<void>;
}

export function Onboarding({ onComplete }: OnboardingProps) {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<OnboardingState>(DEFAULT_ONBOARDING_STATE);
  const [locationMode, setLocationMode] = useState<"choice" | "manual" | "loading" | "denied">("choice");
  const [manualLat, setManualLat] = useState("");
  const [manualLng, setManualLng] = useState("");
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string>();
  const { preference, setPreference } = useTheme();
  const progress = Math.max(0, step - 1);

  const finish = async () => {
    const completedState = { ...state, completed: true, completedAt: new Date().toISOString() };
    setFinishing(true);
    setFinishError(undefined);
    try {
      await onComplete(completedState);
    } catch (cause) {
      setFinishError(cause instanceof Error ? cause.message : "Your workspace could not be prepared.");
      setFinishing(false);
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationMode("denied");
      return;
    }
    setLocationMode("loading");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setState((current) => ({
          ...current,
          location: {
            source: "automatic",
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: coords.accuracy,
            updatedAt: new Date().toISOString()
          }
        }));
        setStep(5);
      },
      () => setLocationMode("denied"),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 86_400_000 }
    );
  };

  const saveManualLocation = () => {
    const latitude = Number(manualLat);
    const longitude = Number(manualLng);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return;
    setState((current) => ({ ...current, location: { source: "manual", latitude, longitude, updatedAt: new Date().toISOString() } }));
    setStep(5);
  };

  const selectedCount = state.starterCollections.length;
  const screen = (() => {
    if (step === 0) {
      return (
        <section className="welcome-screen">
          <div className="welcome-screen__copy">
            <div className="brand-lockup brand-lockup--large"><BrandMark /><span>{APP_NAME}</span></div>
            <p className="welcome-screen__kicker">A quieter place to remember</p>
            <h1>{APP_TAGLINE}</h1>
            <p className="welcome-screen__lede">Gather personal du'as, prepare focused sessions, and preserve reflections over the years—without creating an account.</p>
            <div className="welcome-screen__actions">
              <Button size="lg" onClick={() => setStep(1)} icon={<ArrowRight size={18} />}>Open my workspace</Button>
              <span><LockKeyhole size={15} /> Stored locally in your browser</span>
            </div>
          </div>
          <div className="welcome-preview" aria-hidden="true">
            <div className="welcome-preview__glow" />
            <div className="preview-window">
              <div className="preview-window__top"><i /><i /><i /></div>
              <p>Assalamu alaikum</p>
              <h2>A gentle place for what matters.</h2>
              <div className="preview-session"><MoonStar size={20} /><span><small>Your focus</small>Begin a du'a session</span><ArrowRight size={18} /></div>
              <div className="preview-lines"><span /><span /><span /></div>
              <div className="preview-note"><Sparkles size={16} /> Rediscover a du'a from your journey</div>
            </div>
          </div>
        </section>
      );
    }

    if (step === 1) {
      return (
        <OnboardingPanel icon={<BookHeart />} eyebrow="Welcome" title="Everything you ask Allah for, held with care." description="Capture a thought in seconds, organize it when you are ready, and return to it in a focused moment.">
          <div className="principle-grid">
            <Principle icon={<BookHeart />} title="Your private workspace" body="Personal du'as, people, sessions and reflections in one calm workspace." />
            <Principle icon={<MoonStar />} title="Prepared, not pressured" body="Gentle focus and rediscovery without scores, streaks, or judgment." />
          </div>
          <OnboardingActions step={step} setStep={setStep} />
        </OnboardingPanel>
      );
    }

    if (step === 2) {
      return (
        <OnboardingPanel icon={<ShieldCheck />} eyebrow="Private by design" title="Your data stays here." description="There is no account, email, cloud database, analytics tracker, or server receiving what you write.">
          <div className="privacy-callout">
            <CloudOff size={25} />
            <div><strong>Local and offline-first</strong><p>Clearing browser data can remove your workspace, so the app will help you make portable backups.</p></div>
          </div>
          <OnboardingActions step={step} setStep={setStep} />
        </OnboardingPanel>
      );
    }

    if (step === 3) {
      return (
        <OnboardingPanel icon={<Sparkles />} eyebrow="Make it yours" title="Choose a comfortable reading experience." description="You can change every setting later.">
          <div className="preference-stack">
            <div className="preference-row"><div><strong>Appearance</strong><span>Follow your device or choose a theme.</span></div><div className="segmented">{(["system", "light", "dark"] as const).map((theme) => <button className={cn(preference === theme && "is-active")} onClick={() => setPreference(theme)} key={theme}>{theme}</button>)}</div></div>
            <div className="preference-row"><div><strong>Arabic text</strong><span>Show Arabic in sourced entries.</span></div><button className={cn("toggle", state.showArabic && "is-on")} role="switch" aria-checked={state.showArabic} onClick={() => setState((current) => ({ ...current, showArabic: !current.showArabic }))}><span /></button></div>
            <div className="preference-row"><div><strong>Transliteration</strong><span>Show when an entry includes it.</span></div><button className={cn("toggle", state.showTransliteration && "is-on")} role="switch" aria-checked={state.showTransliteration} onClick={() => setState((current) => ({ ...current, showTransliteration: !current.showTransliteration }))}><span /></button></div>
          </div>
          <OnboardingActions step={step} setStep={setStep} />
        </OnboardingPanel>
      );
    }

    if (step === 4) {
      return (
        <OnboardingPanel icon={<LocateFixed />} eyebrow="Optional context" title="Location can prepare the right moment." description="Coordinates let this device calculate prayer-related contexts such as the final third of the night. Du'a Workspace does not upload the coordinates it receives.">
          {locationMode === "manual" ? (
            <div className="manual-location">
              <div className="manual-location__fields">
                <InputField label="Latitude" inputMode="decimal" placeholder="25.2048" value={manualLat} onChange={(event) => setManualLat(event.target.value)} />
                <InputField label="Longitude" inputMode="decimal" placeholder="55.2708" value={manualLng} onChange={(event) => setManualLng(event.target.value)} />
              </div>
              <Button onClick={saveManualLocation} disabled={!manualLat || !manualLng}>Use these coordinates</Button>
            </div>
          ) : (
            <div className="location-actions">
              <Button size="lg" onClick={requestLocation} disabled={locationMode === "loading"} icon={<LocateFixed size={18} />}>{locationMode === "loading" ? "Requesting location…" : "Allow location"}</Button>
              <Button size="lg" variant="secondary" onClick={() => setLocationMode("manual")}>Enter manually</Button>
              {locationMode === "denied" ? <p role="status">Location was unavailable. The rest of your workspace works normally.</p> : null}
            </div>
          )}
          <div className="onboarding-actions"><Button variant="ghost" onClick={() => setStep(3)} icon={<ArrowLeft size={17} />}>Back</Button><Button variant="ghost" onClick={() => setStep(5)}>Skip for now</Button></div>
        </OnboardingPanel>
      );
    }

    return (
      <OnboardingPanel icon={<Check />} eyebrow="A helpful beginning" title="Which collections would you like ready?" description="Choose any that fit. They are ordinary local collections—you can rename, reorder, or remove them later.">
        <div className="collection-choices">
          {COLLECTIONS.map((collection) => {
            const selected = state.starterCollections.includes(collection);
            return <button key={collection} className={cn("choice-chip", selected && "is-selected")} onClick={() => setState((current) => ({ ...current, starterCollections: selected ? current.starterCollections.filter((item) => item !== collection) : [...current.starterCollections, collection] }))}>{selected ? <Check size={15} /> : null}{collection}</button>;
          })}
        </div>
        {finishError ? <p className="onboarding-error" role="alert">{finishError}</p> : null}
        <div className="onboarding-actions"><Button variant="ghost" disabled={finishing} onClick={() => setStep(4)} icon={<ArrowLeft size={17} />}>Back</Button><Button size="lg" disabled={finishing} onClick={() => void finish()}>{finishing ? "Preparing workspace…" : `Enter my workspace${selectedCount > 0 ? ` · ${selectedCount} collections` : ""}`}</Button></div>
      </OnboardingPanel>
    );
  })();

  return (
    <main className={cn("onboarding", step === 0 && "onboarding--welcome") }>
      {step > 0 ? <header className="onboarding__header"><div className="brand-lockup"><BrandMark /><span>{APP_NAME}</span></div><div className="onboarding__progress" aria-label={`Step ${progress} of 5`}><span style={{ width: `${progress * 20}%` }} /></div><span>{progress} / 5</span></header> : null}
      <AnimatePresence mode="wait"><motion.div key={step} className="onboarding__stage" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: .25 }}>{screen}</motion.div></AnimatePresence>
    </main>
  );
}

function BrandMark() { return <span className="brand-mark"><span /><span /><span /></span>; }

function Principle({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return <div className="principle"><span>{icon}</span><div><strong>{title}</strong><p>{body}</p></div></div>;
}

function OnboardingPanel({ icon, eyebrow, title, description, children }: { icon: React.ReactNode; eyebrow: string; title: string; description: string; children: React.ReactNode }) {
  return <section className="onboarding-panel"><div className="onboarding-panel__icon">{icon}</div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p className="onboarding-panel__description">{description}</p><div className="onboarding-panel__content">{children}</div></section>;
}

function OnboardingActions({ step, setStep }: { step: number; setStep: (step: number) => void }) {
  return <div className="onboarding-actions"><Button variant="ghost" onClick={() => setStep(step - 1)} icon={<ArrowLeft size={17} />}>Back</Button><Button onClick={() => setStep(step + 1)} icon={<ArrowRight size={17} />}>Continue</Button></div>;
}
